import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import {
  toShiftPendingPostBookingRequirementsInput,
  usePendingPostBookingRequirementsForShifts,
} from "@src/appV2/redesign/Documents/api/wrappers/usePendingPostBookingRequirementsForShifts";
import { getOrientationPayDelta } from "@src/appV2/redesign/Shift/PreShiftOrientation/getOrientationPayDelta";
import { useBookedShiftOrientation } from "@src/appV2/redesign/Shift/PreShiftOrientation/useBookedShiftOrientation";
import { type ShiftBoost } from "@src/appV2/redesign/Shift/ShiftBoost/api/schema";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { type Bookings } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { type Shift, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";
import { generatePath } from "react-router-dom";

import { useIsInHomeEnabled } from "../../InHome/lib/useIsInHomeEnabled";
import { getFormattedShiftPayBreakdown } from "../../Shift/getFormattedShiftPayBreakdown";
import { WorkerShiftCard } from "../../Shift/WorkerShift/Card";
import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { SHIFT_DETAILS_FULL_PATH } from "../path";
import { WorkerInterviewCard } from "./WorkerInterviewCard";

interface ScheduledBookingsProps {
  bookings: Bookings[];
  workersForOverlappingShiftSlotByShiftId: Record<string, WorkersForOverlappingShiftSlots>;
}

interface ScheduledShiftBookingRowProps {
  shift: Shift;
  appliedShiftBoost?: ShiftBoost;
  qualificationDisplayName: string;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
}

function ScheduledShiftBookingRow(props: ScheduledShiftBookingRowProps) {
  const { shift, appliedShiftBoost, qualificationDisplayName, workersForOverlappingShiftSlot } =
    props;

  const shiftLengthInHours = differenceInMinutes(parseISO(shift.end), parseISO(shift.start)) / 60;

  const { hasPreShiftOrientation, orientationLengthInMinutes } = useBookedShiftOrientation({
    id: shift._id,
    start: shift.start,
  });

  const hourlyPay = shift.offer?.pay.value ?? shift.finalPay ?? 0;
  const orientationPayDelta = getOrientationPayDelta({
    hourlyPay,
    hasPreShiftOrientation,
    orientationLengthInMinutes,
  });
  const adjustedOriginalAmount = isDefined(shift.originalAmount)
    ? shift.originalAmount + orientationPayDelta
    : shift.originalAmount;

  const payBreakdown = getFormattedShiftPayBreakdown(
    shift.offer?.pay.value,
    shift.finalPay,
    adjustedOriginalAmount,
    appliedShiftBoost?.attributes.amount,
    shiftLengthInHours,
    shift.facility.requiresLunchBreak
  );

  const workplaceId = shift.facility.userId;
  const qualification = shift.agentReq;

  const shiftInputs = useMemo(() => [toShiftPendingPostBookingRequirementsInput(shift)], [shift]);

  const { resultsByShiftId } = usePendingPostBookingRequirementsForShifts(shiftInputs);
  const pendingPostBookingDocumentsCount = resultsByShiftId[shift._id]?.count ?? 0;

  return (
    <WorkerShiftCard
      shiftId={shift._id}
      href={generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId: shift._id })}
      workplaceId={workplaceId ?? ""}
      workplaceName={shift.facility.name}
      workplaceTimezone={shift.facility.tmz ?? ""}
      workplaceRequiresLunchBreak={shift.facility.requiresLunchBreak ?? false}
      shiftTimeSlot={shiftPeriodToTimeSlot[shift.name]}
      shiftStart={shift.start}
      shiftEnd={shift.end}
      shiftQualification={qualification}
      shiftQualificationName={qualificationDisplayName}
      shiftHourlyPay={payBreakdown?.hourlyPay}
      shiftTotalPay={payBreakdown?.totalPay}
      shiftTotalWithBoostPay={payBreakdown?.totalWithBoostPay}
      workersForOverlappingShiftSlot={workersForOverlappingShiftSlot}
      pendingPostBookingDocumentsCount={pendingPostBookingDocumentsCount}
    />
  );
}

export function ScheduledBookings(props: ScheduledBookingsProps) {
  const { bookings, workersForOverlappingShiftSlotByShiftId } = props;

  const isInHomeRedesignEnabled = useIsInHomeEnabled();
  const worker = useDefinedWorker();
  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });
  const { data: shiftBoosts } = useGetShiftBoosts(
    {
      workerId: worker.userId,
      statuses: ["APPLIED"],
    },
    { enabled: shiftBoostConfig.enabled }
  );

  const allQualificationNames = bookings
    .filter((booking) => booking.type === "shift")
    .map((booking) => booking.data.agentReq);

  const { qualificationNameToDisplayLabelMap } =
    useGetQualificationDisplayLabels(allQualificationNames);

  return (
    <Stack spacing={4}>
      {bookings.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={4} sx={{ paddingX: 2 }}>
          <Title semibold component="h2" variant="h5" paddingY={3}>
            {isInHomeRedesignEnabled ? "Other scheduled" : "Scheduled"}{" "}
          </Title>
          <Text semibold variant="body2" color={(theme) => theme.palette.text.tertiary}>
            {bookings.length}
          </Text>
        </Stack>
      )}

      {bookings.map((booking) => {
        if (booking.type === "shift") {
          const shift = booking.data;

          const appliedShiftBoost = shiftBoosts?.data.find(
            (boost) => boost.relationships.shift?.data.id === shift._id
          );
          return (
            <ScheduledShiftBookingRow
              key={shift._id}
              shift={shift}
              appliedShiftBoost={appliedShiftBoost}
              qualificationDisplayName={
                qualificationNameToDisplayLabelMap.get(shift.agentReq) ?? shift.agentReq
              }
              workersForOverlappingShiftSlot={workersForOverlappingShiftSlotByShiftId[shift._id]}
            />
          );
        }

        if (booking.type === "interview") {
          return (
            <WorkerInterviewCard
              key={booking.data.id}
              interviewId={booking.data.id}
              start={booking.data.start}
              end={booking.data.end}
              status={booking.data.status}
              placementId={booking.data.placementId}
              workerTypes={booking.data.placement?.workerTypes ?? []}
              workplaceName={booking.data.workplace?.name ?? ""}
              workplaceDistance={booking.data.workplace?.distance}
            />
          );
        }

        return null;
      })}
    </Stack>
  );
}
